import { BaseFeature } from '../../core/base-feature.js';

/**
 * DossierFeature - Intelligence Dossier management (Frontend)
 *
 * Handles:
 * - Fetching full dossier state (sections, questions, promises)
 * - Updating sections and emotional model
 * - Managing questions and promises
 * - Event subscriptions for React components
 */
export class DossierFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.eventSubscribers = new Map();
        this.pendingRequests = new Map();
        this.cachedDossier = null;
    }

    static getMetadata() {
        return {
            id: 'dossier',
            name: 'Intelligence Dossier',
            version: 'v1.0',
            capabilities: [
                'dossier',
                'view_dossier',
                'manage_dossier'
            ],
            dependencies: []
        };
    }

    async onActivate() {
        console.log('[Dossier] Feature activated');
    }

    async onDeactivate() {
        console.log('[Dossier] Feature deactivated');
    }

    /**
     * Subscribe to dossier events
     */
    subscribe(eventType, callback) {
        if (!this.eventSubscribers.has(eventType)) {
            this.eventSubscribers.set(eventType, new Set());
        }
        this.eventSubscribers.get(eventType).add(callback);
        return () => {
            this.eventSubscribers.get(eventType)?.delete(callback);
        };
    }

    /**
     * Emit event to subscribers
     */
    emitEvent(eventType, data) {
        const subscribers = this.eventSubscribers.get(eventType);
        if (subscribers) {
            subscribers.forEach(cb => {
                try { cb(data); } catch (e) { console.error('Dossier event handler error:', e); }
            });
        }
    }

    /**
     * Handle incoming messages from desktop
     */
    handleMessage(message) {
        const { cmd, action, payload, responseToId } = message;
        const command = cmd || action;

        // Handle response to pending request
        // Desktop sendResponse puts the ID in payload.originalMessageId
        const matchId = responseToId || payload?.originalMessageId;
        if (matchId && this.pendingRequests.has(matchId)) {
            const { resolve } = this.pendingRequests.get(matchId);
            this.pendingRequests.delete(matchId);
            resolve(payload);
            return;
        }

        switch (command) {
            case 'dossier_updated':
                this.emitEvent('dossier_updated', payload);
                break;
            case 'emotional_model_changed':
                this.emitEvent('emotional_model_changed', payload);
                break;
            default:
                console.log('[Dossier] Unknown command:', command);
        }
    }

    /**
     * Send a request and wait for response
     */
    sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const metadata = this.constructor.getMetadata();
            const messageId = this.core.sendFeatureMessage({
                feature: metadata.id,
                version: metadata.version,
                cmd: action,
                payload
            });

            this.pendingRequests.set(messageId, { resolve, reject });

            // Timeout after 15 seconds
            setTimeout(() => {
                if (this.pendingRequests.has(messageId)) {
                    this.pendingRequests.delete(messageId);
                    reject(new Error('Request timeout'));
                }
            }, 15000);
        });
    }

    // ==================== API Methods ====================

    async getFullDossier() {
        return this.sendRequest('dossier_get_full');
    }

    async getSection(sectionKey) {
        return this.sendRequest('dossier_get_section', { sectionKey });
    }

    /**
     * Fetch the full evidence array for a single item. Used by the drawer
     * which lazy-loads on open; the initial dossier payload only contains
     * the 20 newest evidence entries per item.
     */
    async getEvidenceForItem(sectionKey, itemName) {
        return this.sendRequest('dossier_get_evidence_for_item', { sectionKey, itemName });
    }

    async updateSection(sectionKey, content) {
        return this.sendRequest('dossier_update_section', { sectionKey, content });
    }

    async getEmotionalModel() {
        return this.sendRequest('dossier_get_emotional_model');
    }

    async updateEmotionalModel(deltas) {
        return this.sendRequest('dossier_update_emotional_model', { deltas });
    }

    async getQuestions(options = {}) {
        return this.sendRequest('dossier_get_questions', options);
    }

    async addQuestion(questionText, context = null) {
        return this.sendRequest('dossier_add_question', { questionText, context });
    }

    async resolveQuestion(id, notes = null) {
        return this.sendRequest('dossier_resolve_question', { id, notes });
    }

    async getPromises(options = {}) {
        return this.sendRequest('dossier_get_promises', options);
    }

    async addPromise(promiseText, context = null) {
        return this.sendRequest('dossier_add_promise', { promiseText, context });
    }

    async updatePromise(id, status, notes = null) {
        return this.sendRequest('dossier_update_promise', { id, status, notes });
    }

    async getHiddenInterests() {
        return this.sendRequest('dossier_get_hidden_interests');
    }

    async getDossierContext() {
        return this.sendRequest('dossier_get_context');
    }

    async getAnalyses(limit = 20) {
        return this.sendRequest('dossier_get_analyses', { limit });
    }

    async getStats() {
        return this.sendRequest('dossier_get_stats');
    }

    async consolidate() {
        return this.sendRequest('dossier_consolidate');
    }

    async getManagerState() {
        return this.sendRequest('dossier_get_manager_state');
    }

    async consolidateDuplicates() {
        return this.sendRequest('dossier_consolidate_duplicates');
    }

    async revertAnalysis(analysisId) {
        return this.sendRequest('dossier_revert_analysis', { analysisId });
    }

    async revertAnalysisSection(analysisId, sectionKey) {
        return this.sendRequest('dossier_revert_analysis_section', { analysisId, sectionKey });
    }

    async addInterest(interest, category, evidence) {
        return this.sendRequest('dossier_add_interest', { interest, category, evidence });
    }

    async addFetish(fetish, category, source, evidence) {
        return this.sendRequest('dossier_add_fetish', { fetish, category, source, evidence });
    }

    // ==================== Item Actions ====================

    async removeInterest(name) {
        return this.sendRequest('dossier_remove_interest', { name });
    }

    async confirmInterest(name, note = null) {
        return this.sendRequest('dossier_confirm_interest', { name, note });
    }

    async rejectInterest(name) {
        return this.sendRequest('dossier_reject_interest', { name });
    }

    async removeFetish(name) {
        return this.sendRequest('dossier_remove_fetish', { name });
    }

    async confirmFetish(name, note = null) {
        return this.sendRequest('dossier_confirm_fetish', { name, note });
    }

    async rejectFetish(name) {
        return this.sendRequest('dossier_reject_fetish', { name });
    }
}
